import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatMoney, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CATEGORY_COLORS } from "@/lib/colors";
import { Kicker, LoadingCard, ErrorCard, StatusPill, CATEGORIES } from "@/components/common";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Check, X, Pencil, Trash2, Save, XCircle } from "lucide-react";

export default function ExpenseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/expenses/${id}`);
      setExp(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load expense");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const act = async (status) => {
    setActing(true);
    try {
      const res = await api.patch(`/expenses/${id}/status`, { status });
      setExp(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Action failed");
    } finally {
      setActing(false);
    }
  };

  const startEdit = () => {
    setEditForm({
      amount: String(exp.amount ?? ""),
      currency: exp.currency || "CAD",
      category: exp.category || "Other",
      customer: exp.customer || "",
      notes: exp.notes || "",
      merchant: exp.merchant || "",
      date: exp.date || new Date().toISOString().slice(0, 10),
    });
    setEditError("");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditForm(null);
    setEditError("");
  };

  const setField = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  const saveEdit = async () => {
    setEditError("");
    const amount = parseFloat(editForm.amount);
    if (!amount || amount <= 0) {
      setEditError("Amount must be greater than 0");
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/expenses/${id}`, {
        amount,
        currency: editForm.currency,
        category: editForm.category,
        customer: editForm.customer || null,
        notes: editForm.notes || null,
        merchant: editForm.merchant || null,
        date: editForm.date,
      });
      setExp(res.data);
      setEditing(false);
      setEditForm(null);
    } catch (e) {
      setEditError(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/expenses/${id}`);
      nav("/expenses", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed");
      setDeleting(false);
    }
  };

  if (loading)
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <LoadingCard />
      </div>
    );
  if (error)
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <ErrorCard message={error} onRetry={load} />
      </div>
    );
  if (!exp) return null;

  const isManager = user?.role === "manager";
  const isOwner = user?.id === exp.user_id;
  const canApproveReject = isManager && exp.status === "pending";
  const canModify = isManager || (isOwner && exp.status === "pending");
  const cat = CATEGORY_COLORS[exp.category] || CATEGORY_COLORS.Other;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 animate-fade-up">
      <button
        data-testid="detail-back-btn"
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-end justify-between mb-6 gap-4">
        <div className="min-w-0">
          <Kicker>Expense detail</Kicker>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 mt-1 truncate">
            {exp.merchant || exp.category}
          </h1>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <StatusPill status={exp.status} />
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border ${cat.bg} ${cat.text} ${cat.border}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${cat.dot}`} /> {exp.category}
            </span>
            <span className="text-sm text-zinc-500">Submitted {formatDate(exp.created_at)}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="label-kicker">Amount</div>
          <div className="font-display text-3xl font-semibold tracking-tight text-zinc-950 tabular-nums mt-1">
            {formatMoney(exp.amount, exp.currency)}
          </div>
        </div>
      </div>

      {/* Edit / Delete action bar */}
      {canModify && !editing ? (
        <div className="mb-4 flex items-center justify-end gap-2">
          <button
            data-testid="detail-edit-btn"
            onClick={startEdit}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="detail-delete-btn"
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove the expense
                  {exp.merchant ? ` from ${exp.merchant}` : ""}{" "}
                  ({formatMoney(exp.amount, exp.currency)}). This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="delete-confirm-btn"
                  onClick={doDelete}
                  disabled={deleting}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  {deleting ? "Deleting…" : "Delete expense"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-zinc-200 bg-white p-2">
          {exp.receipt_base64 ? (
            <img
              data-testid="detail-receipt-img"
              src={exp.receipt_base64}
              alt="Receipt"
              className="w-full max-h-[560px] object-contain rounded-lg"
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-sm text-zinc-500">No receipt attached</div>
          )}
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            {editing ? (
              <EditForm
                form={editForm}
                setField={setField}
                onSave={saveEdit}
                onCancel={cancelEdit}
                saving={saving}
                error={editError}
              />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-5">
                  <Field label="Employee" value={exp.user_name} />
                  <Field label="Email" value={exp.user_email} />
                  <Field label="Date" value={formatDate(exp.date)} />
                  <Field label="Category" value={exp.category} />
                  <Field label="Merchant" value={exp.merchant || "—"} />
                  <Field label="Customer" value={exp.customer || "—"} />
                  <Field label="Currency" value={exp.currency} />
                  <Field label="Amount" value={formatMoney(exp.amount, exp.currency)} />
                </div>
                {exp.notes ? (
                  <div className="mt-5 pt-5 border-t border-zinc-100">
                    <div className="label-kicker">Notes</div>
                    <p className="mt-2 text-sm text-zinc-800 whitespace-pre-wrap">{exp.notes}</p>
                  </div>
                ) : null}
                {exp.reviewed_by ? (
                  <div className="mt-5 pt-5 border-t border-zinc-100">
                    <div className="label-kicker">Reviewed</div>
                    <p className="mt-2 text-sm text-zinc-800">
                      By <span className="font-medium text-zinc-950">{exp.reviewed_by}</span>
                      {exp.reviewed_at ? ` · ${formatDate(exp.reviewed_at)}` : ""}
                    </p>
                  </div>
                ) : null}
              </>
            )}
          </div>

          {canApproveReject && !editing ? (
            <div className="rounded-xl border border-zinc-200 bg-white p-6 flex items-center justify-between">
              <div>
                <div className="label-kicker">Manager actions</div>
                <p className="mt-1 text-sm text-zinc-600">Approve or reject this expense.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="detail-reject-btn"
                  disabled={acting}
                  onClick={() => act("rejected")}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white text-red-700 px-4 py-2 text-sm hover:bg-red-50 disabled:opacity-60"
                >
                  <X className="w-4 h-4" /> Reject
                </button>
                <button
                  data-testid="detail-approve-btn"
                  disabled={acting}
                  onClick={() => act("approved")}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 text-white px-4 py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
                >
                  <Check className="w-4 h-4" /> Approve
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="label-kicker">{label}</div>
      <div className="mt-1 text-sm text-zinc-900">{value}</div>
    </div>
  );
}

function EditForm({ form, setField, onSave, onCancel, saving, error }) {
  return (
    <div data-testid="edit-form" className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <label className="text-xs font-medium text-zinc-600">Amount</label>
          <input
            data-testid="edit-amount-input"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => setField("amount", e.target.value)}
            required
            className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950 font-display text-lg tabular-nums"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">Currency</label>
          <div className="mt-1 grid grid-cols-2 gap-1 rounded-lg border border-zinc-200 p-1 bg-zinc-50">
            {["CAD", "USD"].map((c) => (
              <button
                type="button"
                key={c}
                data-testid={`edit-currency-${c}-btn`}
                onClick={() => setField("currency", c)}
                className={`rounded-md py-1.5 text-sm font-medium ${
                  form.currency === c ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-500 hover:text-zinc-800"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-zinc-600">Category</label>
        <div className="mt-1 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              type="button"
              key={c}
              data-testid={`edit-category-${c}`}
              onClick={() => setField("category", c)}
              className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                form.category === c
                  ? "bg-zinc-950 text-white border-zinc-950"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-zinc-600">Merchant</label>
          <input
            data-testid="edit-merchant-input"
            value={form.merchant}
            onChange={(e) => setField("merchant", e.target.value)}
            className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">Date</label>
          <input
            data-testid="edit-date-input"
            type="date"
            value={form.date}
            onChange={(e) => setField("date", e.target.value)}
            className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-zinc-600">Customer</label>
        <input
          data-testid="edit-customer-input"
          value={form.customer}
          onChange={(e) => setField("customer", e.target.value)}
          className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950"
        />
      </div>

      <div>
        <label className="text-xs font-medium text-zinc-600">Notes</label>
        <textarea
          data-testid="edit-notes-input"
          value={form.notes}
          onChange={(e) => setField("notes", e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950"
        />
      </div>

      {error ? (
        <div data-testid="edit-error" className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          data-testid="edit-cancel-btn"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm hover:bg-zinc-50 disabled:opacity-60"
        >
          <XCircle className="w-3.5 h-3.5" /> Cancel
        </button>
        <button
          type="button"
          data-testid="edit-save-btn"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-950 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-60"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
